import type { FC, ReactNode, ElementType, CSSProperties } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
    as?: ElementType
}

const Reveal: FC<RevealProps> = ({ children, className, delay = 0, as: Tag = 'div' }): ReactNode => {
    const ref = useRef<HTMLElement | null>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    setVisible(true)
                    observer.disconnect()
                }
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    const style: CSSProperties = delay ? { transitionDelay: `${delay}ms` } : {}

    return (
        <Tag
            ref={ref}
            data-visible={visible ? 'true' : 'false'}
            className={cn('ad-reveal', className)}
            style={style}
        >
            {children}
        </Tag>
    )
}

export default Reveal
